import {
  Box,
  Grid,
  IconButton,
  List,
  ListSubheader,
  makeStyles,
  styled,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/HighlightOffRounded';
import {
  clearActiveChatQuickMessages,
  fetchActiveChatQuickMessages,
  setShowQuickMsg,
} from '@reducers/activeChatQuickMessages';
import { activeChatQuickMessagesSelector } from '@selectors/activeChatQuickMessages';
import {
  QuickMessageFilesFormatType,
  QuickMessageType,
} from '@src/components/AdminHome/QuickMessages/types';
import { parseMessage } from '@src/locales/intl';
import { MessagingProvider } from '@src/model/frontendmodel';
import { fetchDepartments } from '@src/reducers/departments';
import { ChatTray } from '@src/types/chats';
import { QuickMessage } from '@types';
import { isValidProvider } from '@utils/providerLocationUtils';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import QuickMessageListItem from './QuickMessageListItem';

const useStyles = makeStyles(theme => ({
  title: {
    color: theme.palette.text.primary,
    fontWeight: 600,
    padding: '14px 14px 5px',
    position: 'sticky',
    top: '0px',
    zIndex: 2,
    backgroundColor:
      theme.palette.type === 'dark' ? '#1E2128' : theme.palette.grey[100],
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'space-between',
  },
}));

const REGEX_COMMAND = /^(\/[\w-]*)/i;

// Note: do not list QM with files in Instagram
function checkForInstagram(provider: MessagingProvider, message: QuickMessage) {
  if (provider !== MessagingProvider.INSTAGRAM) {
    return true;
  }

  const hasInvalidFiles = message.files?.fileDetails?.some(
    file => file.fileUrl.split('.').pop() === QuickMessageFilesFormatType.PDF
  );

  return !hasInvalidFiles;
}

function filterQuickMessages(
  messages: QuickMessage[],
  command: string,
  types: string[],
  provider: MessagingProvider
) {
  return messages
    .filter(
      message =>
        message.command
          .toLocaleLowerCase()
          .startsWith(command.toLocaleLowerCase()) &&
        types.includes(message.type) &&
        checkForInstagram(provider, message)
    )
    .sort((a, b) => a.command.localeCompare(b.command));
}

const ListSubheaderStyled = styled(ListSubheader)(({ theme }) => ({
  fontSize: '1rem',
  background: theme.palette.type === 'dark' ? '#1E2128' : '#F4F4F5',
  position: 'initial',
}));

export type QuickMessageListProps = {
  command: string;
  activeChat: ChatTray;
  onMessageSelected(message: QuickMessage): void;
};

const lblTitle = parseMessage('ChatMessageComposer.quickMessagesTitle');
const lblNoResults = parseMessage('QuickMessageList.NoResults');

const QuickMessageList: React.FC<QuickMessageListProps> = ({
  activeChat: { id, provider },
  command: command_,
  onMessageSelected,
}) => {
  const { title } = useStyles();

  const { generalMessages, currentMessages, inheritMessages, showQuickMsg } =
    useSelector(activeChatQuickMessagesSelector);

  const dispatch = useDispatch();

  const command = useMemo(
    () => (command_ || '').trimStart().match(REGEX_COMMAND)?.[1] || '',
    [command_]
  );

  const hasCommand = useMemo(() => command && command.length > 0, [command]);

  useEffect(() => {
    if (hasCommand) {
      dispatch(clearActiveChatQuickMessages());
      dispatch(fetchActiveChatQuickMessages());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCommand, id]);

  useEffect(() => {
    dispatch(fetchDepartments());
  }, [dispatch]);

  const validTypes = useMemo(
    () => [
      QuickMessageType.TEXT,
      ...(isValidProvider(provider)
        ? [
            QuickMessageType.LOCATION,
            QuickMessageType.FLOW,
            QuickMessageType.FILES,
          ]
        : []),
    ],
    [provider]
  );

  const generalMessagesItems = filterQuickMessages(
    generalMessages.items,
    command || '',
    validTypes,
    provider
  );

  const currentMessagesItems = filterQuickMessages(
    currentMessages.items,
    command || '',
    validTypes,
    provider
  );

  const inheritMessagesItems = filterQuickMessages(
    inheritMessages.items,
    command || '',
    validTypes,
    provider
  );

  const totalQuickMsg =
    generalMessagesItems.length +
    inheritMessagesItems.length +
    currentMessagesItems.length;

  return (
    <>
      {showQuickMsg && command && (
        <>
          <Box>
            <Box className={title}>
              {lblTitle}
              <IconButton
                color="primary"
                aria-label="close-quick-msg"
                component="span"
                style={{ padding: 0 }}
                onClick={() => {
                  dispatch(setShowQuickMsg(false));
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            {totalQuickMsg === 0 && <Box p="14px">{lblNoResults}</Box>}
          </Box>
          <Grid
            container
            component="div"
            direction="column"
            wrap="nowrap"
            style={{
              maxHeight: `calc(100vh - 400px)`,
              overflow: 'auto',
            }}
          >
            {generalMessagesItems.length > 0 && (
              <List
                subheader={
                  <ListSubheaderStyled color="primary">
                    {parseMessage('QuickMessageList.GeneralSubheader')}
                  </ListSubheaderStyled>
                }
              >
                {generalMessagesItems.map(message => (
                  <QuickMessageListItem
                    key={message.id}
                    command={command}
                    message={message}
                    onClick={onMessageSelected}
                  />
                ))}
              </List>
            )}
            {inheritMessagesItems.length > 0 && (
              <List
                subheader={
                  <ListSubheaderStyled color="primary">
                    {inheritMessages.department}
                  </ListSubheaderStyled>
                }
              >
                {inheritMessagesItems.map(message => (
                  <QuickMessageListItem
                    key={message.id}
                    command={command}
                    message={message}
                    onClick={onMessageSelected}
                  />
                ))}
              </List>
            )}
            {currentMessagesItems.length > 0 && (
              <List
                subheader={
                  <ListSubheaderStyled color="primary">
                    {currentMessages.department}
                  </ListSubheaderStyled>
                }
              >
                {currentMessagesItems.map(message => (
                  <QuickMessageListItem
                    key={message.id}
                    command={command}
                    message={message}
                    onClick={onMessageSelected}
                  />
                ))}
              </List>
            )}
          </Grid>
        </>
      )}
    </>
  );
};

export default QuickMessageList;
